<!-- eslint-disable no-irregular-whitespace -->
<template>
    <div class="sidebar-container">
        <div class="sidebar-header">
            <div class="sidebar-title">
                <div class="title">
                    {{ data.issueReference }}
                </div>
                <md-button class="md-default md-just-icon md-round pull-right" @click="closeModal">
                    <md-icon>close</md-icon>
                </md-button>
            </div>
            <div class="issueReference">
                {{ data.title }}
            </div>
        </div>
        <div class="sidebar-separator" />
        <div class="sidebar-content">
            <div class="sidebar-section">
                <div class="detail-item">
                    <label>Status:</label>
                    <issue-status-button
                        class="sidebar-section-item"
                        :issue="data"
                        @statusUpdated="handleStatusChange"
                    />
                </div>
                <div class="detail-item">
                    <label>Created By:</label>
                    <span class="sidebar-section-item">{{ data.createdByName }}</span>
                </div>
                <div class="detail-item">
                    <label>Created Date:</label>
                    <span class="sidebar-section-item">
                        {{ data.createdDate | dateFormat(DATE_TYPES.standardDateTime) }}
                    </span>
                </div>
                <div class="detail-item">
                    <label>Last Updated By:</label>
                    <span class="sidebar-section-item">{{ data.lastModifiedByName }}</span>
                </div>
                <div class="detail-item">
                    <label>Last Updated:</label>
                    <span class="sidebar-section-item">
                        {{ data.lastModifiedDate | dateFormat(DATE_TYPES.standardDateTime) }}
                    </span>
                </div>
            </div>

            <div class="sidebar-section">
                <h5>Related To</h5>
                <div class="detail-item">
                    <label>Type:</label>
                    <span class="sidebar-section-item">{{ data.relatedEntityType }}</span>
                </div>
                <div class="detail-item">
                    <label>Reference:</label>
                    <span class="sidebar-section-item">
                        <issue-routed-entity :item="data" />
                    </span>
                </div>
            </div>
            <div class="sidebar-separator" />

            <div class="sidebar-section logs-section">
                <h5>Issue Logs</h5>
                <div class="issue-logs">
                    <div v-for="(log, index) in data.issueLogs" :key="index" class="issue-log">
                        <div class="log-header">
                            <span class="log-author">{{ log.createdByName }}</span>
                            <span class="log-date">
                                {{ log.lastModifiedDate | dateFormat(DATE_TYPES.standardDateTime) }}
                            </span>
                        </div>
                        <div>
                            {{ log.note }}
                        </div>
                    </div>
                </div>
                <div class="add-log">
                    <md-field>
                        <label>Add Note</label>
                        <md-textarea class="add-log-textarea" v-model="newLogText"></md-textarea>
                    </md-field>
                    <md-button class="md-primary" @click="addLog">Add</md-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { DATE_TYPES } from '@/utils/constants';
import { handleRequests, showErrorMessage } from '@/helpers';
import { GeneralMixin } from '@/mixins';
import { IssueStatusButton } from './Buttons';
import { IssueRoutedEntity } from './Components';

export default {
    name: 'IssueSidebar',
    components: {
        IssueStatusButton,
        IssueRoutedEntity
    },
    props: {
        issueId: {
            type: Number,
            required: true
        },
        data: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            DATE_TYPES,
            newLogText: ''
        };
    },
    mixins: [GeneralMixin],
    methods: {
        closeModal() {
            this.$emit('closeModal');
        },
        handleStatusChange(response) {
            this.$emit('changeIssueStatus', response);
        },
        async addLog() {
            if (!this.newLogText.trim()) {
                this.$notify({
                    message: 'Please enter a note',
                    type: 'warning'
                });
                return;
            }

            try {
                this.$_handleLoaderState(true, '...ADDING');
                const api = '/api/issue/update-issue';
                const payload = {
                    method: 'post',
                    data: {
                        title: this.data.title,
                        issueReference: this.data.issueReference,
                        issueId: this.data.issueId,
                        issueStatusId: this.data.issueStatusId,
                        teamId: this.data.teamId,
                        tripDate: this.data.tripDate,
                        createdByUserId: this.data.createdByUserId,
                        createdDate: this.data.createdDate,
                        lastModifiedByUserId: this.data.lastModifiedByUserId,
                        lastModifiedDate: this.data.lastModifiedDate,
                        newIssueLogs: [
                            {
                                issueId: this.data.issueId,
                                note: this.newLogText.trim()
                            }
                        ]
                    }
                };

                const response = await handleRequests(api, payload);

                if (response && response.data) {
                    this.$emit('showAddNotes', response.data);

                    this.$notify({
                        message: 'Note added successfully',
                        type: 'success'
                    });
                }
            } catch (error) {
                const message = 'Could not add log.';
                showErrorMessage(this, message, error);
            } finally {
                this.$_handleLoaderState(false);
                this.newLogText = '';
            }
        }
    }
};
</script>

<style scoped lang="scss">
.sidebar-container {
    position: fixed;
    top: 0;
    right: 0;
    width: 600px;
    height: 97vh;
    background-color: white;
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    overflow-y: auto;
    margin-top: 15px;
    border-top-left-radius: 20px;
    border-bottom-left-radius: 20px;
}

.sidebar-header {
    padding: 0 20px;
}

.sidebar-separator {
    border-bottom: 1px solid #ddd;
    width: calc(100% - 32px);
    margin-left: 10px;
    margin-bottom: 20px;
}

.sidebar-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.sidebar-content {
    padding: 0 20px 20px;
}

.sidebar-section {
    margin-bottom: 25px;
    display: block;

    .label {
        display: inline-block;
        width: 95px;
        margin-right: 4px;
        vertical-align: top;
        font-weight: 400;
    }

    .sidebar-section-item {
        display: inline-block;
        vertical-align: top;

        a {
            color: #2b93ff;
        }

        i {
            color: #aaa;
        }
    }
}

.sidebar-section h5 {
    margin-top: 0;
    margin-bottom: 10px;
    font-weight: 400;
    color: #333;
}

.detail-item {
    margin-bottom: 8px;
}

.detail-item label {
    font-weight: 400;
    margin-right: 5px;
    display: inline-block;
    width: 180px;
}

.issue-logs {
    max-height: 300px;
    overflow-y: auto;
    margin-bottom: 15px;
}

.issue-log {
    padding: 10px;
    border: 1px solid #eee;
    border-radius: 4px;
    margin-bottom: 10px;
}

.log-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 5px;
    font-size: 0.9em;
}

.log-author {
    font-weight: 500;
}

.log-date {
    color: #666;
}

.log-content {
    white-space: pre-wrap;
}

.add-log {
    margin-top: 15px;
}

.file-upload {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.sidebar-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 30px;
}

.sidebar-actions button {
    margin-left: 10px;
}

@media screen and (max-width: 768px) {
    .sidebar-container {
        width: 100%;
        border-radius: 0;
        margin-top: 0;
    }

    .sidebar-header {
        padding: 0 15px;
    }

    .sidebar-content {
        padding: 0 15px 15px;
    }

    .detail-item label {
        width: 140px;
    }
}

.issueReference {
    text-transform: uppercase !important;
    font-weight: 400 !important;
    padding: 2px 16px !important;
    font-size: 14px !important;
}

.title {
    text-transform: uppercase !important;
    font-weight: 600 !important;
    padding: 2px 16px !important;
    font-size: 17px !important;
}

.add-log-textarea {
    resize: none !important;
}
</style>

<!-- eslint-enable no-irregular-whitespace -->
